import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { createDialog, CommandService } from '@patsnap/tahiti-core';
import type {
    CommandContribution,
    CommandRegistry,
} from '@patsnap/tahiti-core';
import type {
    PlanListDialogOptions,
    PlanListDialogResult,
} from '../../components/PlanListDialog.vue';
import type { EditionType } from '../../types';
import { isUserLogin } from '@telecom/shared/startup/user-auth';
import { RavenService } from '@telecom/shared/services/raven-service/raven-service';
import * as TrackEvents from '@/constants/track-events';
import { TRACK_EVENT_TYPE } from '@/constants';

// Re-export types for external use
export type { PlanListDialogOptions, PlanListDialogResult };

/**
 * 打开套餐列表对话框的参数
 */
export interface OpenPlanListDialogPayload {
    /** 对话框标题 */
    title?: string;
    /** 支付成功后跳转的 URL */
    successUrl?: string;
    /** 取消支付后跳转的 URL */
    cancelUrl?: string;
    /** 推荐的套餐 ID */
    recommendedPlanId?: EditionType;
    /** 触发来源 */
    trigger_source?: string;
    /** Agent 名称 */
    agent_name?: string;
}

/**
 * PlanList 对话框服务工厂
 */
const planListDialogServiceFactory = createDialog(() => import('../../components/PlanListDialog.vue'));

/**
 * PlanList 对话框命令处理器
 */
@injectable()
export class PlanListDialogCommandHandler implements CommandContribution {
    private _dialogService = planListDialogServiceFactory();

    constructor(
        @inject(CommandService)
        private readonly _commandService: CommandService,
        @inject(RavenService)
        private readonly _ravenService: RavenService,
    ) {}

    /**
     * 处理打开 PlanList 对话框命令
     */
    private async _handleOpenPlanListDialog(payload?: OpenPlanListDialogPayload): Promise<PlanListDialogResult> {
        return new Promise((resolve, reject) => {
            const dialog = this._dialogService;

            dialog.onSubmit(async (result, next) => {
                try {
                    console.log('PlanList dialog submitted:', result);
                    resolve(result);
                    next();
                } catch (error) {
                    next(false);
                    reject(error);
                }
            });

            dialog.onCancel(() => {
                const result: PlanListDialogResult = {
                    action: 'cancel',
                };
                resolve(result);
            });

            this._ravenService.track(TrackEvents.TRACK_EVENT_UPGRADE_TRIGGER, {
                eventType: TRACK_EVENT_TYPE.EurekaUpgradePage,
                eventDetailStr: {
                    product_line: 'eureka_ip',
                    trigger_source: payload?.trigger_source,
                    agent_name: payload?.agent_name,
                },
            });
            dialog.open({
                title: payload?.title,
                width: '1200px',
                height: '800px',
                successUrl: payload?.successUrl,
                cancelUrl: payload?.cancelUrl,
                recommendedPlanId: payload?.recommendedPlanId,
            });
        });
    }

    /**
     * 注册命令
     */
    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand({
            id: 'plg:open-plan-list-dialog',
        }, {
            execute: async (payload?: OpenPlanListDialogPayload) => {
                const isLogin = await isUserLogin();
                if (!isLogin) {
                    // 如果未登录，先打开登录对话框
                    await this._commandService.executeCommand('login:open-dialog', {
                        trigger_source: payload?.trigger_source,
                    });
                    return;
                }
                return this._handleOpenPlanListDialog(payload);
            },
        });
    }
}

declare module '@patsnap/tahiti-core' {
    export interface CommandService {
        /**
         * 打开 PLG 套餐选择对话框
         * @param commandId - 命令 ID
         * @param params - 参数
         */
        executeCommand(commandId: 'plg:open-plan-list-dialog', params?: OpenPlanListDialogPayload): Promise<PlanListDialogResult>;
    }
}
